import { FileText, Mic, GitCompareArrows, Download, ArrowRight, CheckCircle2, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';

const WORKFLOW_STEPS = [
  {
    icon: FileText,
    label: 'Surgical Plan',
    description: 'Pre-op CPT bundles, diagnoses, and payer authorization captured in a structured case wizard.',
    detail: 'ICD-10 + CPT coded',
  },
  {
    icon: Mic,
    label: 'Dictation',
    description: 'Surgeon dictates once in the OR. Structured note auto-generated — no re-keying.',
    detail: 'Immutable transcript',
  },
  {
    icon: GitCompareArrows,
    label: 'Reconciliation',
    description: 'Authorized vs. performed CPTs compared line-by-line. Mismatches flagged before billing.',
    detail: 'Zero-gap enforcement',
  },
  {
    icon: Download,
    label: 'Export',
    description: 'Compliant operative report with audit trail exported to EHR or billing system.',
    detail: 'HIPAA-sealed package',
  },
];

export default function ClosedLoopWorkflowSection() {
  return (
    <section className="py-20 md:py-28 bg-secondary">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header — centered */}
        <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/5 border border-primary/15 text-primary text-xs font-medium mb-6">
            <Shield className="w-3.5 h-3.5" />
            Closed-Loop Revenue Protection
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
            Dictate Once. Reconcile Automatically. Bill With Confidence.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Every surgical case follows an unbreakable chain from pre-op plan to final billing —
            eliminating revenue leakage and documentation gaps.
          </p>
        </div>

        {/* Workflow Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {WORKFLOW_STEPS.map((step, i) => (
            <div key={step.label} className="text-left">
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-md flex items-center justify-center bg-card border border-border">
                  <step.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Step {i + 1}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-foreground mb-3">{step.label}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">{step.description}</p>

              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/5 border border-primary/10 text-xs font-medium text-primary">
                <CheckCircle2 className="w-3 h-3" />
                {step.detail}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom stat bar */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: '100%', label: 'Data Continuity' },
            { value: '<60s', label: 'Surgeon Time' },
            { value: '0', label: 'Revenue Gaps' },
            { value: '§164.312', label: 'HIPAA Compliant' },
          ].map(stat => (
            <div key={stat.label} className="text-center p-4 bg-card border border-border rounded-md">
              <div className="text-xl md:text-2xl font-bold font-mono text-primary">{stat.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
